import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Theme store — os-internal-ui theme + light/dark mode, per-browser.
 *
 * INSPINIA (the app's native look, tokens in globals.css) is the default and
 * is represented by NO data-theme attribute on <html>. The other themes are
 * vendored token override blocks in themes.css keyed by [data-theme] /
 * .dark[data-theme]. Mode is the `.dark` class (see @custom-variant dark in
 * globals.css). index.html has an inline no-FOUC script that reads the same
 * localStorage key before first paint.
 */

export const THEMES = ['inspinia', 'mission-control', 'afterglow', 'reading-room'] as const;
export type ThemeName = (typeof THEMES)[number];
export type ThemeMode = 'light' | 'dark';

export const THEME_LABELS: Record<ThemeName, string> = {
  inspinia: 'Default (INSPINIA)',
  'mission-control': 'Mission Control',
  afterglow: 'Afterglow',
  'reading-room': 'Reading Room',
};

export const THEME_STORAGE_KEY = 'scio3-staff-theme';

function applyToDom(theme: ThemeName, mode: ThemeMode) {
  const el = document.documentElement;
  if (theme === 'inspinia') {
    el.removeAttribute('data-theme');
  } else {
    el.setAttribute('data-theme', theme);
  }
  el.classList.toggle('dark', mode === 'dark');
}

interface ThemeState {
  theme: ThemeName;
  mode: ThemeMode;

  setTheme: (theme: ThemeName) => void;
  setMode: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'inspinia',
      mode: 'light',

      setTheme: (theme) => {
        set({ theme });
        applyToDom(theme, get().mode);
      },
      setMode: (mode) => {
        set({ mode });
        applyToDom(get().theme, mode);
      },
    }),
    { name: THEME_STORAGE_KEY },
  ),
);

/** Re-assert the persisted theme on <html> (boot fallback for the inline script). */
export function applyStoredTheme() {
  const { theme, mode } = useThemeStore.getState();
  applyToDom(theme, mode);
}
